import './ChoosePeriod.css';

import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import Menu from '../Menu';
import PageHeader from '../PageHeader';
import GlobalVariable from '../../GlobalVariable';
import api, {isUnauthorizedError, redirectToLogin} from '../../api';

type PeriodKey = 'week' | 'month' | 'quarter' | 'year' | 'two_years';
type ChartMetric = 'revenue' | 'visits';
type TrendValue = number | null;
type ChartPoint = {x: string; y: number; label: string; fullLabel?: string; visits?: number};
type Option = {value: string; label: string};
type PeriodOption = {value: PeriodKey; label: string};
type Summary = {
    revenue: number;
    appointments: number;
    avgBill: number;
    registeredVisits: number;
    repeatVisits: number;
    daysWorked: number;
    registeredShare: number | null;
    repeatShare: number | null;
};
type Comparison = {
    revenue: TrendValue;
    appointments: TrendValue;
    avgBill: TrendValue;
    repeatShare: TrendValue;
    registeredShare: TrendValue;
    daysWorked: TrendValue;
};
type Meta = {hasData: boolean; periodLabel: string; lastAvailableDate: string | null; sharesNote: string};
type AnalyticsState = {
    loading: boolean;
    error: string;
    selectedPeriod: PeriodKey;
    selectedCompanyId: string;
    periodOptions: PeriodOption[];
    companyOptions: Option[];
    summary: Summary;
    comparison: Comparison;
    revenueChart: ChartPoint[];
    meta: Meta;
    message: string | null;
    chartMetric: ChartMetric;
    selectedPoint: number | null;
};

const PERIODS: PeriodOption[] = [
    {value: 'week', label: 'Неделя'},
    {value: 'month', label: 'Месяц'},
    {value: 'quarter', label: '3 месяца'},
    {value: 'year', label: 'Год'},
    {value: 'two_years', label: '2 года'},
];
const EMPTY_SUMMARY: Summary = {
    revenue: 0, appointments: 0, avgBill: 0, registeredVisits: 0,
    repeatVisits: 0, daysWorked: 0, registeredShare: null, repeatShare: null,
};
const EMPTY_COMPARISON: Comparison = {
    revenue: null, appointments: null, avgBill: null, repeatShare: null,
    registeredShare: null, daysWorked: null,
};
const EMPTY_META: Meta = {
    hasData: false, periodLabel: '1 неделя', lastAvailableDate: null,
    sharesNote: 'Доли считаются по визитам за выбранный период.',
};

function number(value: number): string {
    return value.toLocaleString('ru-RU');
}

function money(value: number): string {
    return number(value) + ' ₽';
}

function visits(value: number): string {
    const lastTwo = value % 100;
    const last = value % 10;
    const word = lastTwo >= 11 && lastTwo <= 14 ? 'визитов'
        : last === 1 ? 'визит' : last >= 2 && last <= 4 ? 'визита' : 'визитов';
    return number(value) + ' ' + word;
}

function percent(value: number | null): string {
    return value === null || !Number.isFinite(value)
        ? '—'
        : value.toLocaleString('ru-RU', {maximumFractionDigits: 1}) + '%';
}

function change(value: TrendValue, points = false): string {
    if (value === null || !Number.isFinite(value)) return 'Нет данных для сравнения';
    if (value === 0) return 'Без изменений';
    return (value > 0 ? '+' : '') + value.toLocaleString('ru-RU', {maximumFractionDigits: 1}) + (points ? ' п.п.' : '%');
}

function changeClass(value: TrendValue): string {
    return value === null || value === 0 ? 'neutral' : value > 0 ? 'up' : 'down';
}

function changeWithContext(value: TrendValue): string {
    return value === null ? 'Нет сравнения' : change(value) + ' к прошлому периоду';
}

function suggestedPeriod(lastDate: string | null, selected: PeriodKey): PeriodKey | null {
    if (!lastDate) return null;
    const parts = lastDate.split('.').map(Number);
    if (parts.length !== 3 || parts.some(part => !Number.isFinite(part))) return null;
    const date = new Date(parts[2], parts[1] - 1, parts[0]);
    if (date.getFullYear() !== parts[2] || date.getMonth() !== parts[1] - 1 || date.getDate() !== parts[0]) return null;
    const age = (Date.now() - date.getTime()) / 86400000;
    if (age < 0) return null;
    if (age < 365 && selected !== 'year' && selected !== 'two_years') return 'year';
    if (age < 730 && selected !== 'two_years') return 'two_years';
    return null;
}

export class Analytics extends Component<any, AnalyticsState> {
    private requestId = 0;
    private chartScrollRef = React.createRef<HTMLDivElement>();

    constructor(props: any) {
        super(props);
        this.state = {
            loading: true,
            error: '',
            selectedPeriod: 'week',
            selectedCompanyId: 'all',
            periodOptions: PERIODS,
            companyOptions: [{value: 'all', label: 'Все салоны'}],
            summary: EMPTY_SUMMARY,
            comparison: EMPTY_COMPARISON,
            revenueChart: [],
            meta: EMPTY_META,
            message: null,
            chartMetric: 'revenue',
            selectedPoint: null,
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        this.loadAnalytics();
    }

    componentDidUpdate(_previousProps: any, previousState: AnalyticsState) {
        if (previousState.revenueChart !== this.state.revenueChart && this.chartScrollRef.current) {
            const lastWithData = this.state.revenueChart.reduce(
                (last, point, index) => point.y > 0 || (point.visits || 0) > 0 ? index : last, -1
            );
            const index = lastWithData >= 0 ? lastWithData : this.state.revenueChart.length - 1;
            this.chartScrollRef.current.scrollLeft = Math.max(0, index * 46 - this.chartScrollRef.current.clientWidth / 2);
        }
    }

    componentWillUnmount() {
        this.requestId += 1;
    }

    loadAnalytics = () => {
        const requestId = ++this.requestId;
        this.setState({loading: true, error: ''});
        api({
            url: GlobalVariable.staffBack() + '/mobile/analytics/staff',
            headers: {'Content-Type': 'application/json'},
            method: 'POST',
            data: {
                period: this.state.selectedPeriod,
                companyId: this.state.selectedCompanyId === 'all' ? null : Number(this.state.selectedCompanyId),
            },
            withCredentials: true,
        }).then(result => {
            if (requestId !== this.requestId) return;
            const payload = result.data || {};
            const chart: ChartPoint[] = payload.charts && Array.isArray(payload.charts.revenue) ? payload.charts.revenue : [];
            this.setState({
                loading: false,
                error: '',
                selectedPeriod: payload.selectedPeriod || this.state.selectedPeriod,
                selectedCompanyId: payload.selectedCompanyId || 'all',
                periodOptions: payload.periods && payload.periods.length
                    ? payload.periods.map((item: PeriodOption) => PERIODS.find(option => option.value === item.value) || item)
                    : PERIODS,
                companyOptions: payload.companyOptions && payload.companyOptions.length
                    ? payload.companyOptions : [{value: 'all', label: 'Все салоны'}],
                summary: payload.summary || EMPTY_SUMMARY,
                comparison: payload.comparison || EMPTY_COMPARISON,
                revenueChart: chart,
                meta: payload.meta || EMPTY_META,
                message: payload.message || null,
                chartMetric: this.state.chartMetric === 'visits' && chart.some(point => typeof point.visits === 'number')
                    ? 'visits' : 'revenue',
                selectedPoint: null,
            });
        }).catch(error => {
            if (requestId !== this.requestId) return;
            if (isUnauthorizedError(error)) {
                redirectToLogin();
                return;
            }
            this.setState({loading: false, error: 'Данные временно недоступны. Попробуйте ещё раз.'});
        });
    };

    handlePeriodChange = (period: PeriodKey) => {
        if (period === this.state.selectedPeriod) return;
        this.setState({selectedPeriod: period, selectedPoint: null}, this.loadAnalytics);
    };

    handleCompanyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({selectedCompanyId: event.target.value, selectedPoint: null}, this.loadAnalytics);
    };

    chartValue = (point: ChartPoint): number =>
        this.state.chartMetric === 'visits' ? Math.max(0, point.visits || 0) : Math.max(0, point.y || 0);

    renderFilters() {
        return (
            <div className='analytics-toolbar'>
                <div className='analytics-toolbar-title'>Мои показатели</div>
                <div className='analytics-toolbar-caption'>Выберите период, чтобы увидеть изменения</div>
                <div className='analytics-periods' role='group' aria-label='Период аналитики'>
                    {this.state.periodOptions.map(period => (
                        <button key={period.value} type='button'
                            className={'analytics-period ' + (this.state.selectedPeriod === period.value ? 'is-active' : '')}
                            aria-pressed={this.state.selectedPeriod === period.value}
                            onClick={() => this.handlePeriodChange(period.value)}>{period.label}</button>
                    ))}
                </div>
                {this.state.companyOptions.length > 1 && (
                    <label className='analytics-company'>
                        <span>Салон</span>
                        <select value={this.state.selectedCompanyId} onChange={this.handleCompanyChange}>
                            {this.state.companyOptions.map(company =>
                                <option key={company.value} value={company.value}>{company.label}</option>
                            )}
                        </select>
                    </label>
                )}
            </div>
        );
    }

    renderEmpty() {
        const {meta, message, selectedPeriod} = this.state;
        const suggestion = suggestedPeriod(meta.lastAvailableDate, selectedPeriod);
        return (
            <div className='analytics-panel analytics-empty-state'>
                <div className='analytics-empty-bars' aria-hidden='true'><i/><i/><i/><i/><i/></div>
                <h2>{message || 'За выбранный период данных пока нет'}</h2>
                <p>{meta.lastAvailableDate
                    ? 'Попробуйте посмотреть более длинный период. Последние доступные данные: ' + meta.lastAvailableDate + '.'
                    : 'Когда поступят сведения об оплаченных визитах, здесь появятся ваши показатели.'}</p>
                {suggestion && <button type='button' className='analytics-action' onClick={() => this.handlePeriodChange(suggestion)}>
                    Показать за {suggestion === 'year' ? 'год' : '2 года'} <span aria-hidden='true'>→</span>
                </button>}
            </div>
        );
    }

    renderChart() {
        const {revenueChart, chartMetric, selectedPoint} = this.state;
        const supportsVisits = revenueChart.some(point => typeof point.visits === 'number');
        const values = revenueChart.map(this.chartValue);
        const max = Math.max(1, ...values);
        const lastWithData = values.reduce((last, value, index) => value > 0 ? index : last, -1);
        const defaultIndex = lastWithData >= 0 ? lastWithData : Math.max(0, values.length - 1);
        const activeIndex = selectedPoint !== null && revenueChart[selectedPoint] ? selectedPoint : defaultIndex;
        const activePoint = revenueChart[activeIndex];
        const activeValue = activePoint ? this.chartValue(activePoint) : 0;
        return (
            <section className='analytics-panel analytics-chart-panel' aria-label='Динамика показателей'>
                <div className='analytics-section-top'>
                    <div>
                        <h2>Динамика</h2>
                        <p>Нажмите на столбец, чтобы узнать значение</p>
                    </div>
                </div>
                <div className='analytics-chart-switch' role='group' aria-label='Показатель на графике'>
                    <button type='button' className={chartMetric === 'revenue' ? 'is-active' : ''}
                        aria-pressed={chartMetric === 'revenue'} onClick={() => this.setState({chartMetric: 'revenue', selectedPoint: null})}>Выручка</button>
                    {supportsVisits && <button type='button' className={chartMetric === 'visits' ? 'is-active' : ''}
                        aria-pressed={chartMetric === 'visits'} onClick={() => this.setState({chartMetric: 'visits', selectedPoint: null})}>Визиты</button>}
                </div>
                <div className='analytics-chart-selected' aria-live='polite'>
                    <strong>{chartMetric === 'revenue' ? money(activeValue) : visits(activeValue)}</strong>
                    <span>{activePoint ? activePoint.fullLabel || activePoint.x : '—'}</span>
                </div>
                <div className='analytics-chart-scroll' ref={this.chartScrollRef}>
                    <div className='analytics-bars' style={{minWidth: Math.max(0, revenueChart.length * 46) + 'px'}}>
                        {revenueChart.map((point, index) => {
                            const value = values[index];
                            const label = chartMetric === 'revenue' ? money(value) : visits(value);
                            return (
                                <button key={index} type='button'
                                    className={'analytics-bar-button ' + (activeIndex === index ? 'is-active' : '')}
                                    onClick={() => this.setState({selectedPoint: index})}
                                    aria-label={(point.fullLabel || point.x) + ': ' + label}
                                    aria-pressed={activeIndex === index}>
                                    <span className='analytics-bar-track'>
                                        <span className='analytics-bar-fill' style={{height: Math.max(4, value / max * 100) + '%'}}/>
                                    </span>
                                    <span className='analytics-bar-label'>{point.x.split('-')[0]}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                {revenueChart.length > 8 && <div className='analytics-swipe-hint'>Листайте график ↔</div>}
            </section>
        );
    }

    renderData() {
        const {summary, comparison, meta} = this.state;
        const repeat = summary.repeatShare === null ? 0 : Math.max(0, Math.min(100, summary.repeatShare));
        return (
            <>
                <section className='analytics-hero' aria-label='Моя выручка за период'>
                    <div className='analytics-hero-top'>
                        <span>Моя выручка за {meta.periodLabel.toLowerCase()}</span>
                        {meta.lastAvailableDate && <small>Данные по {meta.lastAvailableDate}</small>}
                    </div>
                    <strong className='analytics-hero-value'>{money(summary.revenue)}</strong>
                    <div className='analytics-hero-bottom'>
                        <span className={'analytics-change ' + changeClass(comparison.revenue)}>
                            {change(comparison.revenue)}
                        </span>
                        {comparison.revenue !== null && <span className='analytics-change-note'>к прошлому периоду</span>}
                    </div>
                    <div className='analytics-hero-divider'/>
                    <div className='analytics-hero-link'>
                        <span>Выручка по визитам — не зарплата</span>
                        <Link to='/salary'>Расчёт зарплаты <span aria-hidden='true'>→</span></Link>
                    </div>
                </section>

                <div className='analytics-metric-grid'>
                    <section className='analytics-panel analytics-metric'>
                        <span className='analytics-metric-label'>Оплаченные визиты</span>
                        <strong>{number(summary.appointments)}</strong>
                        <span className={'analytics-metric-change ' + changeClass(comparison.appointments)}>
                            {changeWithContext(comparison.appointments)}
                        </span>
                    </section>
                    <section className='analytics-panel analytics-metric'>
                        <span className='analytics-metric-label'>Средний чек</span>
                        <strong>{money(summary.avgBill)}</strong>
                        <span className={'analytics-metric-change ' + changeClass(comparison.avgBill)}>
                            {changeWithContext(comparison.avgBill)}
                        </span>
                    </section>
                </div>

                {this.renderChart()}

                <section className='analytics-panel analytics-clients'>
                    <div className='analytics-section-top'>
                        <div>
                            <h2>Повторные визиты</h2>
                            <p>Клиенты с несколькими записями к вам</p>
                        </div>
                        <strong>{percent(summary.repeatShare)}</strong>
                    </div>
                    <div className='analytics-progress' role='img'
                        aria-label={'Повторные визиты: ' + percent(summary.repeatShare)}>
                        <span style={{width: repeat + '%'}}/>
                    </div>
                    <div className='analytics-clients-foot'>
                        <span>{number(summary.repeatVisits)} из {number(summary.appointments)} визитов</span>
                        <span className={'analytics-metric-change ' + changeClass(comparison.repeatShare)}>
                            {change(comparison.repeatShare, true)}
                        </span>
                    </div>
                </section>

                <details className='analytics-panel analytics-more'>
                    <summary>Ещё о периоде <span aria-hidden='true'>⌄</span></summary>
                    <div className='analytics-more-row'>
                        <span>Дней с оплаченными визитами</span><strong>{number(summary.daysWorked)}</strong>
                    </div>
                    <div className='analytics-more-row'>
                        <span>Визиты с профилем клиента</span><strong>{number(summary.registeredVisits)}</strong>
                    </div>
                    <p>Средний чек — выручка, разделённая на число оплаченных визитов. Повторные визиты считаются по записям клиентов, которые обращались к мастеру более одного раза. Это не показатель возврата клиентов после первого визита.</p>
                </details>
            </>
        );
    }

    renderContent() {
        if (this.state.loading) return (
            <div className='analytics-panel analytics-loading' role='status'>
                <span className='analytics-loading-dot' aria-hidden='true'/>Загружаем ваши показатели…
            </div>
        );
        if (this.state.error) return (
            <div className='analytics-panel analytics-error' role='alert'>
                <div className='analytics-error-icon' aria-hidden='true'>↻</div>
                <h2>Не удалось загрузить аналитику</h2>
                <p>{this.state.error}</p>
                <button type='button' className='analytics-action' onClick={this.loadAnalytics}>Повторить <span aria-hidden='true'>→</span></button>
            </div>
        );
        return this.state.meta.hasData ? this.renderData() : this.renderEmpty();
    }

    render() {
        return (
            <div className='main analytic'>
                <div className='mx-w-content'>
                    <PageHeader pageTitle='Аналитика' backBtnUrl='/'/>
                    <div className='pt-header'/>
                    <div className='analytics-page-content'>
                        {this.renderFilters()}
                        {this.renderContent()}
                    </div>
                </div>
                <Menu/>
            </div>
        );
    }
}

export default Analytics;
